import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  LayoutDashboard, ShoppingCart, Grid3x3, ChefHat, BookOpen, ClipboardList,
  MessageSquareHeart, Users, Package, Truck, BarChart3, UserCog, Settings as SettingsIcon,
  Shield, LogOut, Menu as MenuIcon, ChevronDown, Store, KeyRound, HelpCircle, Wallet, CalendarCheck, DatabaseBackup, Bike, ClipboardPaste
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import api from '../lib/api';
import { toast } from 'sonner';
import GuidedTour from './GuidedTour';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, perm: 'reports' },
  { to: '/pos', label: 'POS Billing', icon: ShoppingCart, perm: 'pos' },
  { to: '/tables', label: 'Tables', icon: Grid3x3, perm: 'tables' },
  { to: '/kds', label: 'Kitchen (KDS)', icon: ChefHat, perm: 'kds', newTab: true },
  { to: '/orders', label: 'Orders', icon: ClipboardList, perm: 'orders' },
  { to: '/aggregator', label: 'Aggregators', icon: Bike, perm: 'orders' },
  { to: '/delivery-import', label: 'Delivery Import', icon: ClipboardPaste, perm: 'settings' },
  { to: '/menu', label: 'Menu', icon: BookOpen, perm: 'menu' },
  { to: '/customers', label: 'Customers', icon: Users, perm: 'customers' },
  { to: '/feedback-list', label: 'Feedback', icon: MessageSquareHeart, perm: 'customers' },
  { to: '/inventory', label: 'Inventory', icon: Package, perm: 'inventory' },
  { to: '/purchasing', label: 'Purchasing', icon: Truck, perm: 'purchasing' },
  { to: '/expenses', label: 'Expenses', icon: Wallet, perm: 'reports' },
  { to: '/day-close', label: 'Day Close', icon: CalendarCheck, perm: 'reports' },
  { to: '/backups', label: 'Backups', icon: DatabaseBackup, perm: 'settings' },
  { to: '/reports', label: 'Reports & AI', icon: BarChart3, perm: 'reports' },
  { to: '/staff', label: 'Staff & Roles', icon: UserCog, perm: 'staff' },
  { to: '/settings', label: 'Settings', icon: SettingsIcon, perm: 'settings' },
];

export default function AppShell({ children }) {
  const { user, tenant, outlets, currentOutlet, selectOutlet, logout, can } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [tourOpen, setTourOpen] = useState(false);
  const [deskOpen, setDeskOpen] = useState(true);

  useEffect(() => {
    if (!user?.is_platform_admin && localStorage.getItem('serveos_tour_seen') !== '1') {
      const t = setTimeout(() => setTourOpen(true), 700);
      return () => clearTimeout(t);
    }
  }, [user]);

  const visibleNav = NAV.filter((n) => user?.is_platform_admin || can(n.perm));

  const doPinLogin = async () => {
    try {
      const { data } = await api.post('/auth/pin-login', { pin });
      localStorage.setItem('serveos_token', data.token);
      toast.success(`Switched to ${data.name} (${data.role})`);
      setPinOpen(false); setPin('');
      window.location.reload();
    } catch (e) { toast.error('Invalid PIN'); }
  };

  const go = (n) => {
    if (n.newTab) { window.open(n.to, '_blank'); return; }
    nav(n.to); setMobileOpen(false);
  };

  const Sidebar = () => (
    <aside className="w-64 shrink-0 bg-sidebar text-sidebar-foreground flex flex-col h-screen sticky top-0">
      <div className="px-5 py-5 flex items-center gap-2 border-b border-white/10">
        <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center font-display font-bold text-primary-foreground">S</div>
        <div>
          <div className="font-display font-bold text-lg leading-none">RoboChef</div>
          <div className="text-[11px] text-sidebar-foreground/60">{tenant?.name || 'Restaurant OS'}</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto no-scrollbar py-3 px-2 space-y-1">
        {user?.is_platform_admin && (
          <button data-testid="nav-admin" onClick={() => { nav('/admin'); setMobileOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${loc.pathname === '/admin' ? 'bg-primary text-primary-foreground' : 'hover:bg-white/5'}`}>
            <Shield className="h-4 w-4" /> Super Admin
          </button>
        )}
        {visibleNav.map((n) => {
          const active = loc.pathname === n.to;
          const Icon = n.icon;
          return (
            <button key={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g,'')}`} onClick={() => go(n)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${active ? 'bg-primary text-primary-foreground' : 'hover:bg-white/5 text-sidebar-foreground/90'}`}>
              <Icon className="h-4 w-4" /> {n.label}
            </button>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="text-xs text-sidebar-foreground/60 px-2 mb-1">Signed in as</div>
        <div className="px-2 mb-2">
          <div className="text-sm font-medium">{user?.name}</div>
          <Badge variant="secondary" className="text-[10px] mt-1 capitalize">{user?.role}</Badge>
        </div>
        <Button data-testid="logout-button" variant="ghost" size="sm" className="w-full justify-start text-sidebar-foreground/80 hover:bg-white/5" onClick={logout}>
          <LogOut className="h-4 w-4 mr-2" /> Logout
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen bg-background">
      <div className={`hidden lg:block ${deskOpen ? '' : 'lg:hidden'}`}><Sidebar /></div>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0"><Sidebar /></div>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b bg-card flex items-center gap-3 px-4 sticky top-0 z-40">
          <Button data-testid="nav-collapse-toggle" variant="ghost" size="icon" onClick={() => { setDeskOpen((v) => !v); setMobileOpen((v) => !v); }}><MenuIcon className="h-5 w-5" /></Button>
          <div className="flex-1" />
          <Button data-testid="open-tour-button" variant="ghost" size="sm" className="gap-2" onClick={() => setTourOpen(true)}>
            <HelpCircle className="h-4 w-4" /> <span className="hidden sm:inline">Guide</span>
          </Button>
          {!user?.is_platform_admin && outlets.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="outlet-selector" variant="outline" size="sm" className="gap-2">
                  <Store className="h-4 w-4" /> {currentOutlet?.name || 'Select Outlet'} <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Switch Outlet</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {outlets.map((o) => (
                  <DropdownMenuItem key={o.id} data-testid={`outlet-option-${o.code}`} onClick={() => selectOutlet(o)}>{o.name}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!user?.is_platform_admin && (
            <Button data-testid="pin-switch-button" variant="outline" size="sm" className="gap-2" onClick={() => setPinOpen(true)}>
              <KeyRound className="h-4 w-4" /> PIN
            </Button>
          )}
        </header>
        <main className="flex-1 min-w-0">{children}</main>
      </div>

      <GuidedTour open={tourOpen} onClose={() => setTourOpen(false)} />

      <Dialog open={pinOpen} onOpenChange={setPinOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>Quick Staff Switch</DialogTitle></DialogHeader>
          <Input data-testid="pin-input" type="password" inputMode="numeric" placeholder="Enter staff PIN" value={pin}
            onChange={(e) => setPin(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doPinLogin()} />
          <Button data-testid="pin-confirm-button" onClick={doPinLogin}>Switch</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
